/**
 * Time-Adjust Audit — admin-only report showing every /jobs/{id}/time PATCH
 * over the last N days with before/after snapshots and the reason field.
 * URL: /admin/time-audit
 *
 * Backend: GET /api/admin/time-adjust-audit?days=30
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api } from "@/lib/api";
import { ClockCounterClockwise, ArrowsClockwise, MagnifyingGlass } from "@phosphor-icons/react";

const FIELD_LABEL = {
  clock_in_at:         "Clock IN",
  clock_out_at:        "Clock OUT",
  en_route_at:         "On-my-way",
  paused_at:           "Paused",
  lunch_start_at:      "Lunch start",
  total_lunch_minutes: "Total lunch (min)",
};

const fmtTs = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "number") return `${v} min`;
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return String(v); }
};

export default function TimeAdjustAudit() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/time-adjust-audit?days=${days}`);
      setData(data);
    } catch { setData({ rows: [], count: 0 }); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const rows = data?.rows || [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(r =>
      (r.admin_name || "").toLowerCase().includes(needle) ||
      (r.reason || "").toLowerCase().includes(needle) ||
      (r.work_order_number || "").toLowerCase().includes(needle) ||
      (r.property_address || "").toLowerCase().includes(needle) ||
      (r.assigned_to_name || "").toLowerCase().includes(needle)
    );
  }, [q, rows]);

  return (
    <AdminShell>
      <div className="p-4 sm:p-8" data-testid="time-audit-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl inline-flex items-center gap-2">
              <ClockCounterClockwise weight="fill" size={28}/> Time-Adjust Audit
            </h1>
            <p className="mt-1 text-sm text-slate-600 font-medium">
              Every clock/lunch/en-route edit made by an admin. Spot suspicious back-dating before it hits payroll.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
              data-testid="time-audit-range"
              className="h-11 px-3 border-2 border-[#0f172a] bg-white text-sm font-black uppercase tracking-widest">
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            <button onClick={load} disabled={busy}
              data-testid="time-audit-refresh"
              className="h-11 px-3 border-2 border-[#0f172a] bg-white hover:bg-slate-100 font-black uppercase text-xs tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
              <ArrowsClockwise weight="bold" size={14}/> {busy ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlass weight="bold" size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search admin, WO#, property, worker, reason…"
              data-testid="time-audit-search"
              className="w-full h-11 pl-8 pr-3 border-2 border-slate-300 text-sm bg-white"/>
          </div>
          <span className="text-xs text-slate-500 font-black uppercase tracking-widest">
            {filtered.length} of {rows.length}
          </span>
        </div>

        <div className="mt-4 space-y-2" data-testid="time-audit-rows">
          {filtered.length === 0 && !busy && (
            <div className="p-8 text-center rugged-card bg-white">
              <div className="text-sm font-semibold text-slate-500">
                No time adjustments in the last {days} days. Good — that means workers are clocking themselves in and out.
              </div>
            </div>
          )}
          {filtered.map(r => (
            <div key={r.id} data-testid={`time-audit-row-${r.id}`}
              className="rugged-card bg-white p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-xs font-black uppercase tracking-widest text-slate-500">
                    {fmtTs(r.created_at)}
                  </div>
                  <div className="mt-1 font-semibold">
                    <span className="text-slate-900">{r.admin_name}</span>
                    <span className="text-slate-500 font-normal"> adjusted </span>
                    {r.job_id ? (
                      <Link to={`/admin/jobs/${r.job_id}`} className="text-cyan-700 hover:underline font-black">
                        {r.work_order_number || "WO"}
                      </Link>
                    ) : (
                      <span className="font-black">{r.work_order_number || "WO"}</span>
                    )}
                    {(r.property_address || r.unit) && (
                      <span className="text-slate-500 font-normal">
                        {" · "}{r.property_address}{r.unit ? ` · Unit ${r.unit}` : ""}
                      </span>
                    )}
                    {r.assigned_to_name && (
                      <span className="text-slate-500 font-normal">{" · Tech: "}<b>{r.assigned_to_name}</b></span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-200">
                      <th className="py-1.5 pr-3">Field</th>
                      <th className="py-1.5 pr-3">Before</th>
                      <th className="py-1.5">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(r.after || {}).map(f => (
                      <tr key={f} className="border-b border-slate-100 last:border-0" data-testid={`time-audit-field-${r.id}-${f}`}>
                        <td className="py-1.5 pr-3 font-black">{FIELD_LABEL[f] || f}</td>
                        <td className="py-1.5 pr-3 text-slate-500 line-through">{fmtTs(r.before?.[f])}</td>
                        <td className="py-1.5 font-semibold text-emerald-700">{fmtTs(r.after?.[f])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {r.reason && (
                <div className="mt-3 p-2 bg-amber-50 border border-amber-200 text-xs" data-testid={`time-audit-reason-${r.id}`}>
                  <span className="font-black uppercase tracking-widest text-amber-800 mr-1.5">Reason:</span>
                  <span className="text-slate-800">{r.reason}</span>
                </div>
              )}
              {!r.reason && (
                <div className="mt-3 text-[11px] italic text-slate-400" data-testid={`time-audit-noreason-${r.id}`}>
                  No reason given
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}
